// =========================================================================
// channel-visuals — the ONE source of truth for how a timeline item is shown:
// its channel (call / email / sms / meeting / note / ticket), the icon + colors
// for that channel, and its direction (inbound / outbound) tag.
//
// WHY THIS EXISTS: the icon used to come from `getTypeIcon(item.type)`, which
// returned MessageSquare for EVERY item — so a call, an email, and a text all
// looked identical, and the same generic icon was re-rendered up to 4× per row
// (header + metadata box + detail header). Channel lives in
// `communication_type.key` (see the `communication_types` table: call, email,
// meeting, note, sms), not in `type`. Every surface that draws a timeline item
// — TimelineItem, MiniTimeline, the detail panel — MUST resolve its icon/label
// through here so they never drift again.
// =========================================================================

import {
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  LifeBuoy,
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  type LucideIcon,
} from "lucide-react";
import type { TimelineItem } from "../hooks/timeline-types";

export type TimelineChannel =
  | "call"
  | "email"
  | "sms"
  | "meeting"
  | "note"
  | "ticket"
  | "unknown";

export interface ChannelVisual {
  channel: TimelineChannel;
  Icon: LucideIcon;
  /** Human label for the channel (e.g. "Text Message"). */
  label: string;
  /** Rail-dot / icon-chip background classes. */
  chipClass: string;
  /** Icon foreground color class. */
  iconClass: string;
}

/** The five real communication channels plus ticket + unknown fallback. */
const CHANNEL_VISUALS: Record<TimelineChannel, Omit<ChannelVisual, "channel">> = {
  call: {
    Icon: Phone,
    label: "Call",
    chipClass: "bg-emerald-100 dark:bg-emerald-950/40",
    iconClass: "text-emerald-600 dark:text-emerald-400",
  },
  email: {
    Icon: Mail,
    label: "Email",
    chipClass: "bg-blue-100 dark:bg-blue-950/40",
    iconClass: "text-blue-600 dark:text-blue-400",
  },
  sms: {
    Icon: MessageSquare,
    label: "Text Message",
    chipClass: "bg-violet-100 dark:bg-violet-950/40",
    iconClass: "text-violet-600 dark:text-violet-400",
  },
  meeting: {
    Icon: Calendar,
    label: "Meeting",
    chipClass: "bg-amber-100 dark:bg-amber-950/40",
    iconClass: "text-amber-600 dark:text-amber-400",
  },
  note: {
    Icon: FileText,
    label: "Note",
    chipClass: "bg-slate-100 dark:bg-slate-800/60",
    iconClass: "text-slate-600 dark:text-slate-300",
  },
  ticket: {
    Icon: LifeBuoy,
    label: "Ticket Update",
    chipClass: "bg-orange-100 dark:bg-orange-950/40",
    iconClass: "text-orange-600 dark:text-orange-400",
  },
  unknown: {
    Icon: Activity,
    label: "Activity",
    chipClass: "bg-muted",
    iconClass: "text-muted-foreground",
  },
};

const KNOWN_CHANNELS = new Set<TimelineChannel>([
  "call",
  "email",
  "sms",
  "meeting",
  "note",
]);

function asChannel(value: unknown): TimelineChannel | null {
  return typeof value === "string" && KNOWN_CHANNELS.has(value as TimelineChannel)
    ? (value as TimelineChannel)
    : null;
}

/**
 * Derive the display channel from an item. Priority:
 *   1. ticket updates → "ticket"
 *   2. `communication_type.key` when it names a known channel
 *   3. `metadata.type` fallback (older rows / mini timeline shape)
 *   4. "unknown"
 */
export function resolveChannel(
  item: Pick<TimelineItem, "type" | "communication_type" | "metadata">,
): TimelineChannel {
  if (item.type === "ticket_update") return "ticket";
  return (
    asChannel(item.communication_type?.key) ??
    asChannel(item.metadata?.type) ??
    "unknown"
  );
}

/**
 * The icon + colors + label for an item's channel. Prefers the row's own
 * `communication_type.label` (already localized, e.g. "Text Message") over the
 * registry default so we show exactly what the CRM stored.
 */
export function getChannelVisual(
  item: Pick<TimelineItem, "type" | "communication_type" | "metadata">,
): ChannelVisual {
  const channel = resolveChannel(item);
  const base = CHANNEL_VISUALS[channel];
  return {
    channel,
    ...base,
    label: item.communication_type?.label?.trim() || base.label,
  };
}

export interface DirectionMeta {
  Icon: LucideIcon;
  label: "In" | "Out";
  className: string;
}

/** Small inbound/outbound tag meta. Returns null when direction is unknown. */
export function getDirectionMeta(
  direction: TimelineItem["direction"],
): DirectionMeta | null {
  if (direction === "inbound") {
    return {
      Icon: ArrowDownLeft,
      label: "In",
      className: "text-emerald-600 dark:text-emerald-400",
    };
  }
  if (direction === "outbound") {
    return {
      Icon: ArrowUpRight,
      label: "Out",
      className: "text-blue-600 dark:text-blue-400",
    };
  }
  return null;
}
